<!-- Toolbar: period + new sale -->
<div class="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
  <div class="sl-periods" role="group" aria-label="Periodo">
    @for (option of periodOptions; track option.value) {
      <button type="button" class="sl-chip" [attr.aria-pressed]="period === option.value"
        (click)="setPeriod(option.value)">
        {{ option.label }}
      </button>
    }
  </div>

  <div class="flex items-center gap-2">
    <button type="button" (click)="toggleFilters()" class="sl-btn sl-btn--ghost inline-flex flex-1 items-center justify-center gap-2 sm:flex-none"
      [attr.aria-expanded]="showFilters" aria-controls="sl-filters">
      <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
      </svg>
      Filtrar
      @if (extraFilterCount > 0) {
        <span class="sl-count">{{ extraFilterCount }}</span>
      }
    </button>
    <button type="button" (click)="openNewSale()" class="sl-btn sl-btn--primary inline-flex flex-1 items-center justify-center gap-2 sm:flex-none">
      <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
      </svg>
      Registrar venta
    </button>
  </div>
</div>

<!-- Filters (dates stay rendered so the pickers can attach) -->
<div id="sl-filters" class="sl-filters" [class.sl-filters--open]="showFilters" [attr.hidden]="showFilters ? null : ''">
  <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
    <div>
      <label class="sl-label" for="fromDate">Desde</label>
      <input #fromDateInput id="fromDate" type="text" placeholder="Cualquier fecha" readonly autocomplete="off"
        class="sl-input w-full cursor-pointer" />
    </div>
    <div>
      <label class="sl-label" for="toDate">Hasta</label>
      <input #toDateInput id="toDate" type="text" placeholder="Cualquier fecha" readonly autocomplete="off"
        class="sl-input w-full cursor-pointer" />
    </div>
    <div class="sl-lookup">
      <app-client-lookup label="Cliente" [showMiniCard]="false" [selectedClient]="selectedClient"
        (clientSelected)="onClientSelected($event)" (clientCleared)="onClientCleared()">
      </app-client-lookup>
    </div>
    <div class="sl-lookup">
      <app-product-lookup label="Producto" [selectedProduct]="selectedProduct"
        (productSelected)="onProductSelected($event)" (productCleared)="onProductCleared()">
      </app-product-lookup>
    </div>
  </div>
  @if (hasActiveFilters) {
    <div class="mt-3 flex justify-end">
      <button type="button" (click)="clearFilters()" class="sl-link">Quitar filtros</button>
    </div>
  }
</div>

<!-- Summary tape -->
<section class="sl-tape" aria-label="Resumen" [class.opacity-60]="loading && sales.length > 0">
  <div class="sl-tape__head">
    <p class="sl-tape__title">
      @if (loading && sales.length === 0) {
        Cargando ventas…
      } @else {
        {{ totalCount }} venta{{ totalCount !== 1 ? 's' : '' }} {{ periodPhrase }}
      }
    </p>
    @if (totalPages > 1) {
      <p class="sl-tape__note">Montos de las {{ sales.length }} ventas de esta página</p>
    }
  </div>
  <dl class="sl-tape__figures">
    <div>
      <dt>Vendido</dt>
      <dd>{{ formatCurrency(totalSalesAmount) }}</dd>
    </div>
    <div>
      <dt><span class="sl-dot sl-dot--paid" aria-hidden="true"></span>Cobrado</dt>
      <dd>{{ formatCurrency(totalPaidAmount) }}</dd>
    </div>
    <div>
      <dt><span class="sl-dot sl-dot--due" aria-hidden="true"></span>Por cobrar</dt>
      <dd [class.sl-due]="totalPendingAmount > 0">{{ formatCurrency(totalPendingAmount) }}</dd>
    </div>
    <div>
      <dt>Venta promedio</dt>
      <dd>{{ formatCurrency(averageTicket) }}</dd>
    </div>
  </dl>
  <div class="sl-meter" role="img" [attr.aria-label]="paidPercent + ' % cobrado'">
    <span class="sl-meter__paid" [style.width.%]="paidPercent"></span>
  </div>
</section>

<!-- Skeleton -->
@if (loading && sales.length === 0) {
  <div class="sl-list" role="status" aria-label="Cargando ventas">
    @for (row of skeletonRows; track $index) {
      <div class="sl-row animate-pulse">
        <div style="grid-area: when"><div class="h-4 w-12 rounded bg-gray-200 dark:bg-gray-800"></div></div>
        <div style="grid-area: who">
          <div class="mb-2 h-4 w-36 rounded bg-gray-200 dark:bg-gray-800"></div>
          <div class="h-3 w-48 max-w-full rounded bg-gray-100 dark:bg-gray-800/70"></div>
        </div>
        <div class="ml-auto h-4 w-20 rounded bg-gray-200 dark:bg-gray-800" style="grid-area: amount"></div>
      </div>
    }
  </div>
}

<!-- Empty -->
@else if (!loading && sales.length === 0) {
  <div class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center dark:border-gray-700 dark:bg-white/[0.03]">
    @if (hasActiveFilters) {
      <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">No hay ventas {{ periodPhrase }}</h3>
      <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">
        Prueba con otro periodo o quita el filtro de cliente o producto.
      </p>
      <button type="button" (click)="clearFilters()" class="sl-btn sl-btn--ghost">Ver todas las ventas</button>
    } @else {
      <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">Aún no has registrado ventas</h3>
      <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">
        Cuando vendas un producto en el local, regístralo aquí para llevar la caja y el inventario al día.
      </p>
      <button type="button" (click)="openNewSale()" class="sl-btn sl-btn--primary inline-flex items-center gap-2">
        <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Registrar venta
      </button>
    }
  </div>
}

<!-- Sales list -->
@else {
  <div class="sl-list" [class.opacity-60]="loading">
    <div class="sl-row sl-row--head" aria-hidden="true">
      <span style="grid-area: when">Fecha</span>
      <span style="grid-area: who">Cliente y productos</span>
      <span style="grid-area: method">Pago</span>
      <span class="text-right" style="grid-area: amount">Total</span>
    </div>
    <ul>
      @for (sale of sales; track sale.paymentId ?? $index) {
        <li class="sl-row">
          <div class="sl-when" style="grid-area: when">
            <span class="sl-when__day">{{ sale.createdAt | date: 'd MMM' }}</span>
            <span class="sl-when__time">{{ sale.createdAt | date: 'h:mm a' }}</span>
          </div>

          <div class="min-w-0" style="grid-area: who">
            <button type="button" class="sl-open" (click)="openDetail(sale)"
              [attr.aria-label]="'Ver venta a ' + (sale.clientName || 'cliente genérico') + ', ' + formatCurrency(sale.amount)">
              <span class="sl-client truncate">{{ sale.clientName || 'Cliente genérico' }}</span>
            </button>
            <p class="sl-items truncate" [title]="getItemsSummary(sale.items)">{{ getItemsSummary(sale.items) }}</p>
          </div>

          <div class="sl-method" style="grid-area: method">
            @if (sale.status !== 'paid') {
              <span class="sl-status" [attr.data-status]="sale.status">{{ getStatusLabel(sale.status) }}</span>
            }
            @if (sale.paidAmount > 0) {
              <span class="sl-method__name">{{ getMethodLabel(sale.paymentMethod) }}</span>
            }
          </div>

          <div class="sl-amount" style="grid-area: amount">
            <p class="sl-amount__total">{{ formatCurrency(sale.amount) }}</p>
            @if (sale.paidAmount < sale.amount) {
              <p class="sl-amount__due">Debe {{ formatCurrency(sale.amount - sale.paidAmount) }}</p>
            }
          </div>

          <div class="sl-actions" style="grid-area: actions">
            <button type="button" (click)="downloadReceipt(sale.paymentId)"
              [disabled]="!sale.paymentId || isDownloadingReceipt(sale.paymentId)"
              class="sl-icon-btn" title="Descargar comprobante" aria-label="Descargar comprobante en PDF">
              @if (isDownloadingReceipt(sale.paymentId)) {
                <span class="sl-spinner" aria-hidden="true"></span>
              } @else {
                <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
              }
            </button>
            <svg class="sl-chevron size-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </div>
        </li>
      }
    </ul>
  </div>

  @if (totalPages > 1) {
    <nav class="mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row" aria-label="Paginación">
      <p class="text-theme-sm text-gray-500 tabular-nums dark:text-gray-400">
        {{ (page - 1) * pageSize + 1 }}–{{ page * pageSize > totalCount ? totalCount : page * pageSize }} de {{ totalCount }}
      </p>
      <div class="flex items-center gap-1">
        <button type="button" (click)="onPageChange(page - 1)" [disabled]="page <= 1" class="sl-page" aria-label="Página anterior">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
        </button>
        @for (p of [].constructor(totalPages); track $index) {
          @if ($index + 1 === page || $index + 1 === 1 || $index + 1 === totalPages || ($index + 1 >= page - 1 && $index + 1 <= page + 1)) {
            <button type="button" (click)="onPageChange($index + 1)" class="sl-page"
              [attr.aria-current]="($index + 1) === page ? 'page' : null">{{ $index + 1 }}</button>
          } @else if ($index + 1 === page - 2 || $index + 1 === page + 2) {
            <span class="inline-flex size-9 items-center justify-center text-sm text-gray-400" aria-hidden="true">…</span>
          }
        }
        <button type="button" (click)="onPageChange(page + 1)" [disabled]="page >= totalPages" class="sl-page" aria-label="Página siguiente">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
        </button>
      </div>
    </nav>
  }
}

<!-- New sale -->
<app-product-sales-modal [open]="showNewSaleModal" (close)="onNewSaleClose()" (saleCompleted)="onSaleCompleted()">
</app-product-sales-modal>

<!-- Sale detail -->
<app-sale-detail-drawer [sale]="selectedSale" [open]="showDetailDrawer"
  [isDownloading]="selectedSale ? isDownloadingReceipt(selectedSale.paymentId) : false" (closed)="closeDetail()"
  (receiptRequested)="downloadReceipt($event.paymentId, $event.format)">
</app-sale-detail-drawer>
